import ErrorBoundary from "@/error-handling/ErrorBoundary";
import RootLayout from "@/layout/RootLayout";
import { AdminRoute } from "@/router/admin-portal-router/AdminRouter";
import lazyRoute, { SuspendedRoute } from "@/router/lazy-route";
import { PatientRoute } from "@/router/patient-portal-router/PatientRouter";
import { ProviderRoute } from "@/router/provider-portal-router/ProviderRouter";
import WidgetRoute from "@/router/widget-router/WidgetRouter";
import { TelehealthRoomPage } from "@/telehealth-v2";
import { getAccessToken, getViewAs } from "@/utils/auth-storage";
import lazyWithRetry from "@/utils/lazy-with-retry";
import { ReactNode } from "react";
import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";

const SuccessLottie = lazyRoute(() => import("@/components/SuccessLottie"));
const AppLayout = lazyRoute(() => import("@/layout/app/AppLayout"));
const PatientEmailedIntakeForm = lazyRoute(
  () =>
    import("@/layout/app/patient-portal/public-view/PatientEmailedIntakeForm")
);
const PublicLayout = lazyRoute(
  () => import("@/layout/app/patient-portal/public-view/PublicLayout")
);
const PublicPatientAssessment = lazyRoute(
  () =>
    import("@/layout/app/patient-portal/public-view/assessments/PublicPatientAssessment")
);
const PublicDocument = lazyRoute(
  () =>
    import("@/layout/app/patient-portal/public-view/document/PublicDocument")
);
const PublicViewEducationMaterialList = lazyRoute(
  () =>
    import("@/layout/app/patient-portal/public-view/education-material/PublicViewEducationMaterialList")
);
const PublicVideoLayout = lazyRoute(
  () =>
    import("@/layout/app/patient-portal/public-view/tele-health/PublicVideoLayout")
);
const VerifyAndJoinTeleHealth = lazyRoute(
  () =>
    import("@/layout/app/patient-portal/public-view/tele-health/VerifyAndJoinTeleHealth")
);
const AuthLayout = lazyRoute(() => import("@/layout/auth/AuthLayout"));
const LockScreen = lazyRoute(() => import("@/pages/auth/LockScreen"));
const ForgotPasswordV2 = lazyRoute(
  () => import("@/pages/auth/ForgotPasswordV2")
);
const LoginV2 = lazyRoute(() => import("@/pages/auth/login"));
const Logout = lazyRoute(() => import("@/pages/auth/Logout"));
const SetAndResetPassword = lazyWithRetry(
  () => import("@/pages/auth/SetAndResetPassword")
);
const TwoFactorAuth = lazyRoute(() => import("@/pages/auth/TwoFactorAuth"));
const VerifyEmail = lazyRoute(() => import("@/pages/auth/VerifyEmail"));
const PreviewZoom = lazyWithRetry(() => import("@/zoom/Preview"));
const DefaultRoutePage = lazyRoute(() => import("@/router/DefaultRoutePage"));

// import Login from "../pages/auth/Login";

interface ProtectedRouteProps {
  children: ReactNode;
  accessingViewAs: "admin" | "provider" | "patient";
}

export function ProtectedRoute({
  children,
  accessingViewAs,
}: Readonly<ProtectedRouteProps>) {
  const token = getAccessToken();
  const actualViewAs = getViewAs();

  if (token && accessingViewAs === actualViewAs) {
    return <>{children}</>;
  } else return <Navigate to="/" />;
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      path="/"
      element={
        <ErrorBoundary>
          <RootLayout />
        </ErrorBoundary>
      }
    >
      <Route path="public" element={<PublicLayout />}>
        <Route path="document" element={<PublicDocument />} />
        <Route
          path="education-material"
          element={<PublicViewEducationMaterialList />}
        />
        <Route path="questions" element={<PublicPatientAssessment />} />
        <Route path="forms/email" element={<PatientEmailedIntakeForm />} />
        <Route path="submit-success" element={<SuccessLottie />} />

        {/* zoom public route for patient */}
        <Route path="video" element={<PublicVideoLayout />}>
          <Route path="preview" element={<VerifyAndJoinTeleHealth />} />
          <Route
            path="subs"
            element={
              <SuspendedRoute>
                <PreviewZoom isPublicView />
              </SuspendedRoute>
            }
          />
        </Route>

        {/* telehealth v2 — guest entry point used by Copy URL / email invite */}
        <Route
          path="telehealth-v2/join"
          element={<TelehealthRoomPage isPublicView />}
        />
      </Route>

      <Route path="auth" element={<AuthLayout />}>
        <Route path="login" element={<LoginV2 />} />
        <Route index path="2FA" element={<TwoFactorAuth />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route
          path="set-password"
          element={
            <SuspendedRoute>
              <SetAndResetPassword linkType={"set"} />
            </SuspendedRoute>
          }
        />
        <Route
          path="reset-password"
          element={
            <SuspendedRoute>
              <SetAndResetPassword linkType={"reset"} />
            </SuspendedRoute>
          }
        />

        <Route path="forgot-password" element={<ForgotPasswordV2 />} />
        <Route path="logout" element={<Logout />} />
        <Route path="lock-screen" element={<LockScreen />} />
      </Route>
      <Route path="app" element={<AppLayout />}>
        {/* <Route path="join-room" element={<DraggableJoinRoom />} /> */}
        <Route
          path="preview"
          element={
            <SuspendedRoute>
              <PreviewZoom />
            </SuspendedRoute>
          }
        />

        {ProviderRoute()}
        {AdminRoute()}
        {WidgetRoute()}
        {PatientRoute()}
      </Route>

      <Route path="*" element={<DefaultRoutePage />} />
    </Route>
  )
);
